import React from 'react';
import { Link } from '@/lib/router-compat';
import { Heart, MapPin } from 'lucide-react';
import { useHomeRecommendations } from '@/hooks/useHomeRecommendations';
import { useCountry } from '@/hooks/useCountry';
import { useLang } from '@/i18n/useLang';
import CardSkeletonGrid from './CardSkeletonGrid';
import { buildListingSlug } from '@/utils/listingSlug';

const COPY = {
  ro: { title: 'Anunțuri recomandate', seeAll: 'Vezi toate →', verified: 'Dealer verificat', privateSeller: 'Vânzător privat', noImg: 'Fără imagine', addFav: 'Adaugă la favorite', promoted: 'Promovat', priceDrop: 'Preț redus', addedToday: 'Adăugat azi' },
  en: { title: 'Recommended listings',  seeAll: 'See all →',  verified: 'Verified dealer', privateSeller: 'Private seller', noImg: 'No image',  addFav: 'Add to favourites', promoted: 'Promoted', priceDrop: 'Price drop', addedToday: 'Added today' },
  de: { title: 'Empfohlene Anzeigen',    seeAll: 'Alle ansehen →', verified: 'Geprüfter Händler', privateSeller: 'Privatverkäufer', noImg: 'Kein Bild', addFav: 'Zu Favoriten', promoted: 'Beworben', priceDrop: 'Preis gesenkt', addedToday: 'Heute hinzugefügt' },
} as const;


const RecommendedListingsV2: React.FC = () => {
  const { country } = useCountry();
  const lang = useLang();
  const t = COPY[lang];
  const { data: listings, isLoading } = useHomeRecommendations(country);

  if (isLoading) {
    return <CardSkeletonGrid count={3} />;
  }

  // Paid/manual selections stay first; rotation fills only unused places.
  const items = listings || [];
  if (items.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-10" data-home-recommended="true">
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-serif text-2xl md:text-3xl">{t.title}</h2>
        <Link to="/vehicles" className="text-sm text-primary hover:underline font-medium">
          {t.seeAll}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {items.map((l: any, idx: number) => {
          const slug = l.slug || buildListingSlug(l as any);
          const photo = l.photos?.[0] || l.first_photo_url;

          // Badge logic (max 2, priority: Promoted > Price drop > Added today)
          const isPromoted = l.promoted === true || l.is_featured === true;
          // The "Promovat" badge means a paid package only — admin-granted free
          // promotions appear in the section without it.
          const isPaidPromotion =
            ['standard', 'promoted', 'until_sold'].includes(l.package_type) ||
            l.promotion_source === 'paid';
          const showPromotedBadge = !l.recommendationOnly && isPromoted && isPaidPromotion;
          const prevPrice = Number(l.previous_price ?? l.original_price ?? 0);
          const curPrice = Number(l.price ?? 0);
          const isPriceDropped =
            l.price_dropped === true ||
            (prevPrice > 0 && curPrice > 0 && curPrice < prevPrice);
          const createdAt = l.created_at ? new Date(l.created_at).getTime() : 0;
          const isAddedToday = createdAt > 0 && (Date.now() - createdAt) < 24 * 3600 * 1000;

          const badges: { label: string; cls: string }[] = [];
          if (showPromotedBadge) badges.push({ label: t.promoted, cls: 'bg-amber-500 text-white' });
          if (isPriceDropped) badges.push({ label: t.priceDrop, cls: 'bg-red-500 text-white' });
          if (isAddedToday) badges.push({ label: t.addedToday, cls: 'bg-green-500 text-white' });
          const shownBadges = badges.slice(0, 2);

          return (
            <Link
              key={l.id}
              to={`/listing/${slug}`}
              preload="viewport"
              className="group rounded-xl bg-card border border-border overflow-hidden hover:shadow-lg transition-all"
            >
              {/* Image */}
              <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                {photo ? (
                  <img
                    src={photo}
                    alt={`${l.make} ${l.model}`}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    width={400}
                    height={300}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">{t.noImg}</div>
                )}
                {shownBadges.length > 0 && (
                  <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                    {shownBadges.map((b) => (
                      <span
                        key={b.label}
                        className={`rounded-md text-xs font-semibold px-2 py-0.5 shadow-xs ${b.cls}`}
                      >
                        {b.label}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); }}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-background/90 backdrop-blur flex items-center justify-center hover:bg-background"
                  aria-label={t.addFav}
                >
                  <Heart className="h-4 w-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-4">
                <h3 className="font-semibold text-base line-clamp-1">
                  {l.make} {l.model} {l.year}
                </h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                  {[l.trim_level, l.mileage_value && `${l.mileage_value.toLocaleString('ro-RO')} km`].filter(Boolean).join(' · ')}
                </p>
                <div className="mt-3 text-xl font-bold text-primary">
                  {Number(l.price).toLocaleString('ro-RO')} {String(l.currency || 'EUR').toUpperCase() === 'EUR' ? '€' : String(l.currency).toUpperCase()}
                </div>

              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="line-clamp-1">
                  {l.seller_type === 'private' ? t.privateSeller : (l.dealer_name || t.verified)}
                </span>

                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {l.location_city || l.location_region || ''}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default RecommendedListingsV2;
